// @givmo/app-admin — Approve / Reject join-request dialog (EP-SUITE2).
//
// One dialog, mode driven by the `mode` prop (mirrors the admins feature's dialog conventions):
//   - APPROVE: the Global Admin assigns the base role the requester lands with (the wire's
//     Owner | Member, DEFAULT Member — least privilege, matching the backend default). Submit →
//     companiesApproveCompanyJoinRequest (POST /admin/company/join-requests/{id}/approve) with
//     body { role }. ⚠ THE TILES READ "Global Admin" / "Member" (the vocabulary rule R6, D-CPO-31):
//     "Global Admin" is the org-level role's name in every user-facing string; "Owner" names a
//     per-module tier only. The VALUES stay the wire's `Owner` / `Member` — the request body is
//     unchanged.
//   - REJECT: an OPTIONAL note. Submit → companiesRejectCompanyJoinRequest (POST …/{id}/reject) with
//     body { note } (blank collapses to undefined — the backend trims it to null).
// On success the inbox list query is invalidated and the dialog closes; a server error surfaces inline.

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { sdk, isGivmoError, type CompanyJoinRequestOut } from '@givmo/api-client';
import {
  Button,
  Dialog,
  ErrorMessage,
  Field,
  RadioTile,
  RadioTileGroup,
  testIdProp,
} from '@givmo/design-system';
import { GLOBAL_ADMIN_LABEL, MEMBER_LABEL } from '@givmo/org-console';
import { ROLE_OWNER, ROLE_MEMBER, OWNER_DESC, MEMBER_DESC } from '../admins/admins-roles';
import { requesterName } from './join-requests-decode';

export type JoinRequestActionMode = 'approve' | 'reject';

export interface JoinRequestActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null when closed; otherwise which action the dialog performs. */
  mode: JoinRequestActionMode | null;
  /** The request being adjudicated; null when closed. */
  request: CompanyJoinRequestOut | null;
  /** The inbox list query key to invalidate on success. */
  invalidateKey: readonly unknown[];
}

export function JoinRequestActionDialog({
  open,
  onOpenChange,
  mode,
  request,
  invalidateKey,
}: JoinRequestActionDialogProps) {
  const queryClient = useQueryClient();
  const isApprove = mode === 'approve';

  const [role, setRole] = useState<string>(ROLE_MEMBER);
  const [note, setNote] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);

  // Re-prime on each open (default role Member — least privilege; blank note).
  useEffect(() => {
    if (!open) return;
    setRole(ROLE_MEMBER);
    setNote('');
    setServerError(null);
  }, [open, mode, request]);

  const approve = useMutation({
    mutationFn: (args: { requestId: string; role: string }) =>
      sdk.companiesApproveCompanyJoinRequest({
        path: { request_id: args.requestId },
        body: { role: args.role },
        throwOnError: true,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: invalidateKey });
      onOpenChange(false);
    },
    onError: (error: unknown) => setServerError(serverMessage(error)),
  });

  const reject = useMutation({
    mutationFn: (args: { requestId: string; note: string }) =>
      sdk.companiesRejectCompanyJoinRequest({
        path: { request_id: args.requestId },
        // Blank collapses to undefined so the body carries no note (backend trims to null).
        body: { note: args.note.trim().length > 0 ? args.note.trim() : undefined },
        throwOnError: true,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: invalidateKey });
      onOpenChange(false);
    },
    onError: (error: unknown) => setServerError(serverMessage(error)),
  });

  const submitting = approve.isPending || reject.isPending;

  const onSubmit = () => {
    if (request === null || mode === null) return;
    setServerError(null);
    if (mode === 'approve') {
      approve.mutate({ requestId: request.id, role });
    } else {
      reject.mutate({ requestId: request.id, note });
    }
  };

  const who = request ? requesterName(request) : '';

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !submitting && onOpenChange(next)}>
      <Dialog.Content
        aria-label={isApprove ? 'Approve join request' : 'Reject join request'}
        className="flex flex-col gap-16"
        testId="join-request-action-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {isApprove ? `Approve ${who}` : `Reject ${who}`}
        </Dialog.Title>

        {isApprove ? (
          <>
            <p className="font-sans text-s14 text-text-tertiary">
              Approving adds {who} as an admin of your company. Choose the role they start with.
            </p>
            <RadioTileGroup
              value={role}
              onValueChange={setRole}
              aria-label="Choose role"
              className="gap-12"
              testId="join-request-role-group"
            >
              <RadioTile
                value={ROLE_MEMBER}
                label={MEMBER_LABEL}
                description={MEMBER_DESC}
                testId="join-request-role-member"
              />
              <RadioTile
                value={ROLE_OWNER}
                label={GLOBAL_ADMIN_LABEL}
                description={OWNER_DESC}
                testId="join-request-role-owner"
              />
            </RadioTileGroup>
          </>
        ) : (
          <Field
            label="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            testId="join-request-reject-note"
          />
        )}

        {serverError !== null && (
          <ErrorMessage testId="join-request-action-error">{serverError}</ErrorMessage>
        )}

        <div className="mt-8 flex items-center justify-end gap-8">
          <Dialog.Close
            disabled={submitting}
            render={<Button appearance="saas" tone="secondary" size="md" />}
            {...testIdProp('join-request-action-cancel')}
          >
            Cancel
          </Dialog.Close>
          <Button
            appearance="saas"
            tone={isApprove ? 'primary' : 'danger'}
            size="md"
            loading={submitting}
            onClick={onSubmit}
            testId="join-request-action-submit"
          >
            {isApprove ? 'Approve' : 'Reject'}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function serverMessage(error: unknown): string {
  return isGivmoError(error) ? error.message : 'Something went wrong. Please try again.';
}
